import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../config/axios';

const formatPrice = (price) =>
  Number(price || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

function MyProductsPage() {
  const [myProducts, setMyProducts] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMyProducts = async () => {
      try {
        const { data } = await axiosInstance.get('/api/products/my-products');
        setMyProducts(data);
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching your listings.');
      } finally {
        setLoading(false);
      }
    };

    fetchMyProducts();
  }, []);

  const totals = useMemo(() => {
    const inventoryValue = myProducts.reduce((acc, product) => acc + product.quantity * product.price, 0);
    const activeListings = myProducts.filter((product) => product.quantity > 0).length;
    return { inventoryValue, activeListings };
  }, [myProducts]);

  const handleDelete = async (productId) => {
    const confirmed = window.confirm('Delete this listing? It will also be removed from carts.');
    if (!confirmed) return;

    try {
      await axiosInstance.delete(`/api/products/${productId}`);
      setMyProducts((products) => products.filter((product) => product._id !== productId));
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting this listing.');
    }
  };

  if (loading) return <div className="page-shell"><div className="state-card">Loading seller dashboard...</div></div>;

  return (
    <div className="page-shell">
      <section className="seller-dashboard">
        <div>
          <span className="eyebrow">Seller dashboard</span>
          <h1>Manage listings and inventory.</h1>
        </div>
        <Link className="button button-primary" to="/list-product">Create Listing</Link>
      </section>

      <div className="dashboard-stats">
        <div>
          <span>{myProducts.length}</span>
          <strong>Total listings</strong>
        </div>
        <div>
          <span>{totals.activeListings}</span>
          <strong>In stock</strong>
        </div>
        <div>
          <span>{formatPrice(totals.inventoryValue)}</span>
          <strong>Inventory value</strong>
        </div>
      </div>

      {error && <div className="notice error">{error}</div>}

      {myProducts.length === 0 ? (
        <div className="state-card">
          <h2>No products listed yet</h2>
          <p>Create your first listing to start selling in the marketplace.</p>
          <Link className="button button-primary" to="/list-product">List a Product</Link>
        </div>
      ) : (
        <section className="seller-list">
          {myProducts.map((product) => (
            <article key={product._id} className="seller-row">
              <img src={product.images?.[0]} alt={product.name} />
              <div>
                <Link to={`/product/${product._id}`}>{product.name}</Link>
                <p>{product.description}</p>
                <span>{formatPrice(product.price)} - {product.quantity} available</span>
              </div>
              <div className="seller-actions">
                <button className="button button-secondary" onClick={() => navigate(`/edit-product/${product._id}`)}>
                  Edit
                </button>
                <button className="button button-danger" onClick={() => handleDelete(product._id)}>
                  Delete
                </button>
              </div>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}

export default MyProductsPage;
